import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useTheme } from '@mui/material/styles';
import { Autocomplete } from '../../Forms/Autocomplete';
import { Box } from '../../Layout/Box';

export interface AssignedToDropdownLabels {
  /** Placeholder text when no user is selected (default: "Unassigned") */
  unassigned?: string;
  /** aria-label on the input (default: "Assigned to") */
  ariaLabel?: string;
  /** aria-label on the clear button (default: "Clear") */
  clear?: string;
}

export interface AssignedToDropdownProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  labels?: AssignedToDropdownLabels;
  'data-testid'?: string;
}

type Option = { value: string; label: string };

export const AssignedToDropdown = ({
  value,
  onChange,
  options,
  disabled = false,
  labels = {},
  'data-testid': testId,
}: AssignedToDropdownProps) => {
  const muiTheme = useTheme();
  const unassignedLabel = labels.unassigned ?? 'Unassigned';
  const ariaLabel = labels.ariaLabel ?? 'Assigned to';
  const clearLabel = labels.clear ?? 'Clear';

  const selectedOption = value != null ? options.find((o) => o.value === value) ?? null : null;

  return (
    <Box
      data-testid={testId}
      sx={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : undefined,
        // Borderless, label-less, compact field (per Figma): no floating label and
        // no outlined border. A transparent border is reserved and turns solid midGray
        // on focus — rectangular (borderRadius 0), so it sits on the field edge
        // instead of as a rounded outline outside it. (The arrow is removed via
        // forcePopupIcon below, which also frees the reserved right padding.)
        '& .MuiInputLabel-root': { display: 'none' },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        // The "Unassigned" placeholder should read as the normal text color, not the
        // browser's faded placeholder, so it matches the selected value's color.
        '& .MuiInputBase-input::placeholder': { opacity: 1 },
        '& .MuiOutlinedInput-root': {
          borderRadius: 0,
          border: '2px solid transparent',
          paddingLeft: 0,
          // At rest, trim the right padding MUI reserves for the clear icon so the
          // name uses more width (no × is shown until focus).
          paddingRight: '12px !important',
        },
        '& .MuiOutlinedInput-root.Mui-focused': {
          borderColor: muiTheme.palette.common.midGray,
          // On focus the clear (×) appears at the right; reserve room so it doesn't
          // sit on top of the selected name.
          paddingRight: '32px !important',
        },
      }}
    >
      <Autocomplete<Option>
        options={options}
        value={selectedOption}
        disabled={disabled}
        // No dropdown arrow (per Figma). forcePopupIcon=false also drops MUI's
        // hasPopupIcon class, so the input no longer reserves ~65px of right padding
        // for an arrow that isn't there (which was clipping the assignee name).
        forcePopupIcon={false}
        // Native clear: the × appears on focus/hover and clears the selection.
        clearText={clearLabel}
        getOptionLabel={(opt) => opt.label}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        onChange={(_event, newValue) => {
          onChange(newValue?.value ?? null);
        }}
        label={ariaLabel}
        textFieldProps={{
          placeholder: selectedOption ? undefined : unassignedLabel,
          inputProps: { 'aria-label': ariaLabel },
          slotProps: {
            input: {
              startAdornment: (
                <AccountCircleIcon sx={{ color: 'action.active', mr: 0.5, fontSize: 28 }} />
              ),
            },
          },
        }}
      />
    </Box>
  );
};

AssignedToDropdown.displayName = 'AssignedToDropdown';
